/**
 * CpConfigModal — iter150AG super-admin Control-Plane config modal.
 *
 * Opens from the "CP: MOCK | LIVE" chip in the Sequencing-System
 * header. Super admins enter the Control-Plane base URL + API key,
 * test the connection (one round-trip diagnostic, no persistence),
 * and either save (persists to Mongo + rebuilds the in-process
 * singleton, so the very next request goes live) or clear back to
 * env / mock.
 *
 * The bearer key is masked in the GET response — the UI only shows
 * the prefix + last 4 chars unless the operator types in a fresh
 * value to replace it.
 */
import { useEffect, useState } from "react";
import {
  X, Plugs, CheckCircle, Warning, CircleNotch, Trash, FloppyDisk, WifiHigh,
  Copy,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  seqCpConfigGet, seqCpConfigSave, seqCpConfigTest, seqCpConfigReset,
  seqCpWebhookStatus, seqCpWebhookVerify,
} from "@/lib/sequencingApi";


export default function CpConfigModal({ open, onClose, onSaved }) {
  const [loading, setLoading]   = useState(true);
  const [persisted, setPersisted] = useState(null);
  const [envState,   setEnvState]   = useState(null);
  const [activeMode, setActiveMode] = useState("mock");
  const [activeSource, setActiveSource] = useState("none");

  const [baseUrl, setBaseUrl] = useState("");
  const [apiKey,  setApiKey]  = useState("");
  // iter150AG Part 12 (P2) — inbound webhook secret + WS consumer toggle.
  const [webhookSecret, setWebhookSecret] = useState("");
  const [wsEnabled, setWsEnabled] = useState(false);
  const [webhookStatus, setWebhookStatus] = useState(null);
  // iter150AG Part 14 — verify diagnostic state.
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifying,    setVerifying]    = useState(false);

  const [testBusy,  setTestBusy]  = useState(false);
  const [testRes,   setTestRes]   = useState(null);

  const [saveBusy,  setSaveBusy]  = useState(false);
  const [resetBusy, setResetBusy] = useState(false);

  // iter150AG Part 12 (P2) — derived URL the operator copies into CP's
  // "Register webhook" form. Computed off ``window.location.origin`` so
  // preview and production deploys each show their own URL.
  const webhookUrl = (typeof window !== "undefined"
                       ? window.location.origin
                       : "") + "/api/sequencing/cp-webhook";

  // iter150AG Part 12 (P2) — clipboard helper with execCommand fallback
  // for environments where the modern Clipboard API isn't permitted
  // (some iframes / older Safari).
  async function copyToClipboard(text, successMsg) {
    try {
      if (typeof navigator !== "undefined"
          && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity  = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success(successMsg || "Copied");
    } catch (e) {
      toast.error("Copy failed — your browser blocked clipboard access.");
      throw e;
    }
  }



  // iter150AG Part 14 — passive verify against recent inbound traffic.
  async function runVerify() {
    setVerifying(true);
    try {
      const r = await seqCpWebhookVerify(300);
      setVerifyResult(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Verify failed");
    } finally { setVerifying(false); }
  }


  async function refresh() {
    setLoading(true);
    try {
      const cfg = await seqCpConfigGet();
      setPersisted(cfg.persisted);
      setEnvState(cfg.env);
      setActiveMode(cfg.mode);
      setActiveSource(cfg.source);
      setBaseUrl(cfg.persisted?.base_url || "");
      setApiKey("");                  // never pre-fill the secret
      setWebhookSecret("");           // ditto for the webhook secret
      setWsEnabled(!!cfg.persisted?.ws_fallback_enabled);
      setTestRes(null);
      // iter150AG Part 12 (P2) — fetch webhook+WS status.
      try {
        const ws = await seqCpWebhookStatus();
        setWebhookStatus(ws);
      } catch { setWebhookStatus(null); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Load failed");
    } finally { setLoading(false); }
  }

  useEffect(() => { if (open) refresh(); /* eslint-disable-next-line */ }, [open]);

  async function runTest() {
    if (!baseUrl.trim() || !apiKey.trim()) {
      toast.error("Enter both base URL and API key first.");
      return;
    }
    setTestBusy(true); setTestRes(null);
    try {
      const res = await seqCpConfigTest(baseUrl.trim(), apiKey.trim());
      setTestRes(res);
      if (res.ok) {
        toast.success(`Connection OK (${res.latency_ms}ms)`);
      } else {
        toast.error(`Connection failed: ${res.detail || "unknown"}`);
      }
    } catch (e) {
      const det = e?.response?.data?.detail || e?.message || "Test failed";
      setTestRes({ ok: false, host_up: false, auth_ok: false, detail: det });
      toast.error(det);
    } finally { setTestBusy(false); }
  }

  async function runSave() {
    if (!baseUrl.trim() || !apiKey.trim()) {
      toast.error("Enter both base URL and API key first.");
      return;
    }
    setSaveBusy(true);
    try {
      // iter150AG Part 12 (P2) — also persist webhook secret + WS toggle
      // when present. Empty webhook secret means "leave as-is".
      const opts = { ws_fallback_enabled: wsEnabled };
      if (webhookSecret.trim()) opts.webhook_secret = webhookSecret.trim();
      const r = await seqCpConfigSave(baseUrl.trim(), apiKey.trim(), opts);
      toast.success(`Saved — Control-Plane mode is now ${r.mode}.`);
      await refresh();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Save failed");
    } finally { setSaveBusy(false); }
  }

  async function runReset() {
    if (!window.confirm("Clear the persisted Control-Plane override? The portal will fall back to environment variables (or mock mode if none).")) return;
    setResetBusy(true);
    try {
      const r = await seqCpConfigReset();
      toast.success(`Cleared — mode is now ${r.mode}.`);
      await refresh();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Reset failed");
    } finally { setResetBusy(false); }
  }

  if (!open) return null;

  return (
    <div data-testid="cp-config-modal"
         className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-slate-900/60 backdrop-blur-sm"
         onClick={onClose}>
      <div className="bg-white w-full max-w-lg rounded-sm shadow-xl border border-slate-200
                       max-h-[92vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <header className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 sticky top-0 bg-white">
          <Plugs size={18} weight="fill" className="text-blue-700" />
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-bold font-['Chivo'] text-slate-900">
              Control-Plane connection
            </h2>
            <p className="text-[10px] text-slate-500">
              Active mode: <b className={`uppercase ${
                activeMode === "live" ? "text-emerald-700" : "text-amber-700"}`}>
                {activeMode}
              </b>
              <span className="text-slate-400 ml-1">(source: {activeSource})</span>
            </p>
          </div>
          <button data-testid="cp-config-close" onClick={onClose}
                  className="w-7 h-7 inline-flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500">
            <X size={14} />
          </button>
        </header>

        {loading ? (
          <div className="p-8 text-slate-500 text-xs flex items-center gap-2">
            <CircleNotch size={14} className="animate-spin" /> Loading…
          </div>
        ) : (
          <div className="p-4 space-y-4">
            {/* Existing persisted summary */}
            {persisted ? (
              <div data-testid="cp-config-persisted"
                   className="bg-emerald-50 border border-emerald-200 rounded-sm p-2.5 text-[11px] text-emerald-900">
                <div className="font-bold uppercase tracking-wider text-[10px] text-emerald-700">
                  Persisted override
                </div>
                <div className="font-mono mt-1">{persisted.base_url}</div>
                <div className="font-mono">key: {persisted.api_key_mask}</div>
                <div className="text-[10px] text-emerald-700 mt-1">
                  set by {persisted.updated_by} on {persisted.updated_at}
                </div>
              </div>
            ) : envState?.base_url_present && envState?.api_key_present ? (
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-2.5 text-[11px] text-slate-700"
                   data-testid="cp-config-env">
                <b>Using environment variables</b> — the portal already has
                <code> CONTROL_PLANE_BASE_URL</code> + <code>CONTROL_PLANE_API_KEY</code> set.
                Override below to point at a different host without a redeploy.
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-2.5 text-[11px] text-amber-900"
                   data-testid="cp-config-mock-warning">
                <b>Mock mode is active.</b> No persisted override and no
                env vars. Enter live credentials below to point this portal
                at a real Control Plane.
              </div>
            )}

            {/* Inputs */}
            <div className="space-y-2">
              <label className="block">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">
                  Control-Plane base URL
                </span>
                <input
                  data-testid="cp-config-baseurl"
                  type="url"
                  placeholder="https://control-plane.example.com"
                  value={baseUrl}
                  onChange={e => setBaseUrl(e.target.value)}
                  className="mt-1 w-full text-xs px-2 py-1.5 border border-slate-300 rounded-sm font-mono" />
              </label>
              <label className="block">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">
                  API key (Bearer)
                </span>
                <input
                  data-testid="cp-config-apikey"
                  type="password"
                  autoComplete="off"
                  placeholder={persisted ? "leave blank to keep current key" : "iotcp_xxxxxxxx_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"}
                  value={apiKey}
                  onChange={e => setApiKey(e.target.value)}
                  className="mt-1 w-full text-xs px-2 py-1.5 border border-slate-300 rounded-sm font-mono" />
                {persisted && !apiKey && (
                  <p className="text-[10px] text-slate-500 mt-1">
                    Current key on file: <code className="font-mono">{persisted.api_key_mask}</code> —
                    type a new value above to rotate it.
                  </p>
                )}
              </label>

              {/* iter150AG Part 12 (P2) — webhook secret + WS fallback. */}
              <label className="block pt-2 border-t border-slate-100">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">
                  Inbound webhook secret
                  <span className="ml-1 text-slate-400 font-normal lowercase tracking-normal">
                    (CP signs every delivery with this)
                  </span>
                </span>
                <div className="mt-1 flex gap-1.5">
                  <input
                    data-testid="cp-config-webhook-secret"
                    type="password"
                    autoComplete="off"
                    placeholder={persisted?.webhook_secret_mask
                      ? "leave blank to keep current secret"
                      : "paste the 32-char secret shown by CP after you create the webhook there"}
                    value={webhookSecret}
                    onChange={e => setWebhookSecret(e.target.value)}
                    className="flex-1 text-xs px-2 py-1.5 border border-slate-300 rounded-sm font-mono" />
                  {webhookSecret && (
                    <button type="button"
                            data-testid="cp-config-webhook-secret-copy"
                            onClick={() => copyToClipboard(webhookSecret, "Secret copied")}
                            title="Copy the value you typed (useful for verifying against CP's display)"
                            className="px-2 py-1 text-[11px] font-bold uppercase tracking-wider rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1">
                      <Copy size={12} weight="bold" />
                      Copy
                    </button>
                  )}
                </div>
                <p className="text-[10px] text-slate-500 mt-1 leading-snug">
                  CP generates this secret automatically on webhook creation and signs every
                  delivery with it. We verify — we never generate. Paste the value CP showed
                  you (and only that value) above, then Save.
                </p>
                {persisted?.webhook_secret_mask && !webhookSecret && (
                  <p className="text-[10px] text-slate-500 mt-1">
                    Current secret on file: <code className="font-mono">{persisted.webhook_secret_mask}</code>.
                    {envState?.webhook_secret_present && (
                      <span className="ml-1 text-blue-700">ENV var wins.</span>
                    )}
                  </p>
                )}
                <div className="mt-1.5 flex items-start gap-1.5">
                  <p className="text-[10px] text-slate-500 flex-1">
                    Register this URL with CP →{" "}
                    <code className="font-mono break-all">{webhookUrl}</code>
                  </p>
                  <button type="button"
                          data-testid="cp-config-webhook-url-copy"
                          onClick={() => copyToClipboard(webhookUrl, "Webhook URL copied")}
                          title="Copy webhook URL"
                          className="shrink-0 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1">
                    <Copy size={10} weight="bold" />
                    Copy URL
                  </button>
                </div>
              </label>

              <label className="flex items-start gap-2 pt-1 cursor-pointer">
                <input
                  data-testid="cp-config-ws-toggle"
                  type="checkbox"
                  checked={wsEnabled}
                  onChange={e => setWsEnabled(e.target.checked)}
                  className="mt-0.5" />
                <span className="text-xs text-slate-700">
                  <b className="text-slate-900">Enable WebSocket fallback channel</b>
                  <span className="block text-[11px] text-slate-500">
                    Opens a long-lived <code>wss://…/api/ws/events</code> connection
                    so events arrive even if your webhook receiver is unreachable.
                    Idempotent against the webhook channel.
                  </span>
                </span>
              </label>

              {webhookStatus && (
                <div data-testid="cp-config-webhook-status"
                      className="bg-slate-50 border border-slate-200 rounded-sm p-2 text-[11px] text-slate-700 space-y-0.5">
                  <div>
                    Webhook secret source:{" "}
                    <code className="font-mono">{webhookStatus.secret_source}</code>
                    {webhookStatus.secret_configured ? (
                      <span className="ml-1 text-emerald-700">✓ configured</span>
                    ) : (
                      <span className="ml-1 text-rose-700">✗ not configured</span>
                    )}
                  </div>
                  <div>
                    Last webhook delivery:{" "}
                    {webhookStatus.last_webhook_age_s == null
                      ? <span className="text-slate-400">none yet</span>
                      : <span>{Math.floor(webhookStatus.last_webhook_age_s)}s ago</span>}
                  </div>
                  <div>
                    WS consumer:{" "}
                    <code className="font-mono">
                      {webhookStatus.ws_consumer?.running ? webhookStatus.ws_consumer.connection_state : "idle"}
                    </code>
                    {webhookStatus.ws_consumer?.events_received > 0 && (
                      <span className="ml-2 text-slate-500">
                        events received: {webhookStatus.ws_consumer.events_received}
                      </span>
                    )}
                  </div>
                  {/* iter150AG Part 14 — Verify button. */}
                  <div className="pt-1.5 mt-1 border-t border-slate-200 flex items-center gap-2">
                    <button type="button"
                            data-testid="cp-config-verify-secret"
                            onClick={runVerify}
                            disabled={verifying || !webhookStatus.secret_configured}
                            title={!webhookStatus.secret_configured
                              ? "Save a webhook secret first"
                              : "Check the last 5 min of inbound CP traffic to confirm the stored secret matches"}
                            className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed">
                      {verifying
                        ? <CircleNotch size={11} weight="bold" className="animate-spin" />
                        : <CheckCircle size={11} weight="bold" />}
                      Verify against recent CP traffic
                    </button>
                  </div>
                  {verifyResult && (
                    <div data-testid="cp-config-verify-result"
                          className={`mt-1.5 rounded-sm border p-2 ${
                            verifyResult.verdict === "ok"
                              ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                              : verifyResult.verdict === "secret_mismatch"
                                ? "border-rose-300 bg-rose-50 text-rose-900"
                                : "border-amber-300 bg-amber-50 text-amber-900"}`}>
                      <div className="font-bold text-xs">
                        {verifyResult.verdict === "ok" && "✓ Secret verified"}
                        {verifyResult.verdict === "secret_mismatch" && "✗ Secret mismatch"}
                        {verifyResult.verdict === "no_traffic" && "⚠ No recent CP traffic"}
                      </div>
                      <div className="text-[11px] leading-snug mt-0.5">
                        {verifyResult.message}
                      </div>
                      <div className="text-[10px] text-current opacity-70 mt-1 font-mono space-y-0.5">
                        <div>last 5 min ─ successes: <b>{verifyResult.successes_in_window}</b> · auth-fails: <b>{verifyResult.auth_failures_in_window}</b></div>
                        {verifyResult.last_authfail_reason && (
                          <div>most-recent auth-fail reason: <b>{verifyResult.last_authfail_reason}</b></div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Test result */}
            {testRes && (
              <div data-testid={`cp-config-test-${testRes.ok ? "ok" : "fail"}`}
                   className={`rounded-sm p-2.5 text-[11px] border ${
                     testRes.ok
                       ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                       : "bg-rose-50 border-rose-200 text-rose-900"}`}>
                <div className="flex items-center gap-2">
                  {testRes.ok
                    ? <CheckCircle size={14} weight="fill" />
                    : <Warning size={14} weight="fill" />}
                  <b>{testRes.ok ? "Connection OK" : "Connection failed"}</b>
                  {testRes.latency_ms != null && (
                    <span className="text-[10px] font-mono ml-auto">{testRes.latency_ms} ms</span>
                  )}
                </div>
                <ul className="mt-1.5 space-y-0.5 text-[10px]">
                  <li>host reachable: <b>{String(testRes.host_up)}</b></li>
                  <li>auth accepted: <b>{String(testRes.auth_ok)}</b></li>
                  {testRes.detail && <li className="font-mono break-all">{testRes.detail}</li>}
                </ul>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-200">
              <button
                data-testid="cp-config-test"
                disabled={testBusy || !baseUrl.trim() || !apiKey.trim()}
                onClick={runTest}
                className="text-xs px-3 py-1.5 rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1">
                {testBusy
                  ? <CircleNotch size={12} className="animate-spin" />
                  : <WifiHigh size={12} weight="bold" />}
                Test connection
              </button>
              <button
                data-testid="cp-config-save"
                disabled={saveBusy || !baseUrl.trim() || !apiKey.trim()}
                onClick={runSave}
                className="text-xs px-3 py-1.5 rounded-sm border border-blue-600 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1">
                {saveBusy
                  ? <CircleNotch size={12} className="animate-spin" />
                  : <FloppyDisk size={12} weight="bold" />}
                Save & apply
              </button>
              {persisted && (
                <button
                  data-testid="cp-config-clear"
                  disabled={resetBusy}
                  onClick={runReset}
                  className="ml-auto text-xs px-2 py-1.5 rounded-sm border border-rose-300 bg-white text-rose-700 hover:bg-rose-50 disabled:opacity-40 inline-flex items-center gap-1">
                  {resetBusy
                    ? <CircleNotch size={12} className="animate-spin" />
                    : <Trash size={12} />}
                  Clear override
                </button>
              )}
            </div>

            <p className="text-[10px] text-slate-500 leading-relaxed">
              Saving persists to MongoDB and rebuilds the in-process Control-Plane
              client — the very next API call uses the new credentials. Clearing
              the override falls back to env vars (or mock if those aren't set
              either).
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
